import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { api } from "../../axios/api";
import Swal from "sweetalert2";
import Select from "react-select";
import { Building2, Camera, Save, Fingerprint } from "lucide-react";

const ProfileEmpresaTab = () => {
  const user = useSelector((state) => state.user.user);
  const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");

  const [empresa, setEmpresa] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoArquivo, setFotoArquivo] = useState(null);

  const [formData, setFormData] = useState({
    nome: "",
    nif: "",
    email: "",
    contacto: "",
    contacto_alternativo: "",
    endereco: "",
    pais_id: "",
    provincia_id: "",
    regime_iva_id: "",
    private_key: "",
  });

  const [loading, setLoading] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [paises, setPaises] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [regimes, setRegimes] = useState([]);

  const texts = {
    "pt-AO": {
      sectionId: "Identificação da Entidade",
      sectionContact: "Contacto e Localização",
      labelLegalName: "Razão Social (Nome Legal)",
      labelNif: "NIF",
      labelTradeName: "Nome Comercial",
      labelEmail: "E-mail de Contacto",
      labelPhone1: "Telefone Principal",
      labelPhone2: "Telefone Secundário",
      labelCountry: "País",
      labelProvince: "Província",
      labelIva: "Regime de IVA",
      labelAddress: "Morada Geográfica",
      labelKey: "Chave Privada (FE)",
      keyHint: "Cole aqui a sua RSA Private Key...",
      keyLocked: "Chave configurada com sucesso e protegida.",
      keyInfo: "Esta chave é necessária para a assinatura digital (AGT).",
      btnUpdate: "Actualizar Configurações",
      btnLogo: "Alterar Logo",
      btnConfirmLogo: "Confirmar Logo",
      saving: "A gravar...",
      successData: "Dados institucionais actualizados.",
      successLogo: "Logotipo actualizado com sucesso.",
      error: "Falha na operação."
    },
    "en-US": {
      sectionId: "Entity Identification",
      sectionContact: "Contact and Location",
      labelLegalName: "Legal Name",
      labelNif: "Tax ID (NIF)",
      labelTradeName: "Trade Name",
      labelEmail: "Contact Email",
      labelPhone1: "Primary Phone",
      labelPhone2: "Secondary Phone",
      labelCountry: "Country",
      labelProvince: "Province",
      labelIva: "VAT Regime",
      labelAddress: "Physical Address",
      labelKey: "Private Key (FE)",
      keyHint: "Paste your RSA Private Key here...",
      keyLocked: "Key successfully configured and protected.",
      keyInfo: "This key is required for digital signature (AGT).",
      btnUpdate: "Update Settings",
      btnLogo: "Change Logo",
      btnConfirmLogo: "Confirm Logo",
      saving: "Saving...",
      successData: "Institutional data updated.",
      successLogo: "Logo updated successfully.",
      error: "Operation failed."
    },
    "zh-CN": {
      sectionId: "实体识别",
      sectionContact: "联系方式与地点",
      labelLegalName: "法定名称",
      labelNif: "纳税识别号 (NIF)",
      labelTradeName: "商业名称",
      labelEmail: "联系电子邮件",
      labelPhone1: "主要电话",
      labelPhone2: "备用电话",
      labelCountry: "国家",
      labelProvince: "省份",
      labelIva: "增值税制度",
      labelAddress: "详细地址",
      labelKey: "私钥 (FE)",
      keyHint: "在此处粘贴您的 RSA 私钥...",
      keyLocked: "密钥配置成功并受保护。",
      keyInfo: "此密钥是数字签名 (AGT) 所必需s的。",
      btnUpdate: "更新设置",
      btnLogo: "更改徽标",
      btnConfirmLogo: "确认徽标",
      saving: "正在保存...",
      successData: "机构数据已更新。",
      successLogo: "徽标更新成功。",
      error: "操作失败。"
    },
    "fr-FR": {
      sectionId: "Identification de l'Entité",
      sectionContact: "Contact et Localisation",
      labelLegalName: "Raison Sociale (Nom Légal)",
      labelNif: "NIF",
      labelTradeName: "Nom Commercial",
      labelEmail: "E-mail de Contact",
      labelPhone1: "Téléphone Principal",
      labelPhone2: "Téléphone Secondaire",
      labelCountry: "Pays",
      labelProvince: "Province",
      labelIva: "Régime TVA",
      labelAddress: "Adresse Géographique",
      labelKey: "Clé Privée (FE)",
      keyHint: "Collez ici votre clé privée RSA...",
      keyLocked: "Clé configurée avec succès et protégée.",
      keyInfo: "Cette clé est requise pour la signature numérique (AGT).",
      btnUpdate: "Mettre à jour les Paramètres",
      btnLogo: "Changer le Logo",
      btnConfirmLogo: "Confirmer le Logo",
      saving: "Enregistrement...",
      successData: "Données institutionnelles mises à jour.",
      successLogo: "Logo mis à jour avec succès.",
      error: "Échec de l'opération."
    },

    "ar-SA": {
      sectionId: "تعريف الكيان",
      sectionContact: "الاتصال والموقع",
      labelLegalName: "الاسم القانوني",
      labelNif: "الرقم الضريبي (NIF)",
      labelTradeName: "الاسم التجاري",
      labelEmail: "البريد الإلكتروني للتواصل",
      labelPhone1: "الهاتف الرئيسي",
      labelPhone2: "الهاتف الثانوي",
      labelCountry: "الدولة",
      labelProvince: "المقاطعة",
      labelIva: "نظام ضريبة القيمة المضافة",
      labelAddress: "العنوان الجغرافي",
      labelKey: "المفتاح الخاص (FE)",
      keyHint: "ألصق مفتاح RSA الخاص هنا...",
      keyLocked: "تم إعداد المفتاح بنجاح وهو محمي.",
      keyInfo: "هذا المفتاح مطلوب للتوقيع الرقمي (AGT).",
      btnUpdate: "تحديث الإعدادات",
      btnLogo: "تغيير الشعار",
      btnConfirmLogo: "تأكيد الشعار",
      saving: "جارٍ الحفظ...",
      successData: "تم تحديث بيانات المؤسسة.",
      successLogo: "تم تحديث الشعار بنجاح.",
      error: "فشلت العملية."
    }
  };

  const t = texts[language] || texts["pt-AO"];

  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const res = await api.get(`/auth/empresas/${user.empresa_id}/`);
        setEmpresa(res.data);
        setFormData({
          nome: res.data.nome || "",
          nif: res.data.nif || "",
          email: res.data.email || "",
          contacto: res.data.contacto || "",
          contacto_alternativo: res.data.contacto_alternativo || "",
          endereco: res.data.endereco || "",
          pais_id: res.data.pais_id || "",
          provincia_id: res.data.provincia_id || "",
          regime_iva_id: res.data.regime_iva_id || "",
          private_key: res.data.private_key || "",
        });
      } catch (err) {
        console.error(err);
      }
    };
    if (user?.empresa_id) fetchEmpresa();
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      const [resPaises, resRegimes] = await Promise.all([
        api.get("/config/paises/"),
        api.get("/config/regimes-iva/")
      ]);
      setPaises(resPaises.data.map((p) => ({ value: p.id, label: p.nome })));
      setRegimes(resRegimes.data.map((r) => ({ value: r.id, label: r.regime })));
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!formData.pais_id) return;
    const fetchProvincias = async () => {
      const res = await api.get(`/config/provincias/pais/${formData.pais_id}/`);
      setProvincias(res.data.map((prov) => ({ value: prov.id, label: prov.nome })));
    };
    fetchProvincias();
  }, [formData.pais_id]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSelectChange = (field, option) => setFormData({ ...formData, [field]: option ? option.value : "" });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFotoArquivo(file);
      setFotoPreview(URL.createObjectURL(file));
    }
  };

  const handleUpdateData = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/auth/empresas/${user.empresa_id}/`, formData);
      Swal.fire("OK", t.successData, "success");
    } catch (err) {
      Swal.fire("Error", t.error, "error");
    } finally { setLoading(false); }
  };

  const handleUpdatePhoto = async () => {
    if (!fotoArquivo) return;
    setLoadingPhoto(true);
    const data = new FormData();
    data.append("file", fotoArquivo);
    try {
      const res = await api.post(`/app/empresas/logotipo/${user.empresa_id}/`, data);
      setEmpresa(res.data);
      setFotoPreview(null);
      Swal.fire("OK", t.successLogo, "success");
    } catch (err) {
      Swal.fire("Error", t.error, "error");
    } finally { setLoadingPhoto(false); }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: "0px",
      borderColor: "#8a8886",
      minHeight: "38px",
      boxShadow: "none",
      "&:hover": { borderColor: "#0078d4" }
    })
  };

  return (
    <>
      <style>{`
        .win10-input { border-radius: 0px !important; border: 1px solid #8a8886 !important; padding: 8px 12px; font-size: 14px; }
        .win10-input:focus { border-color: #0078d4 !important; outline: 1px solid #0078d4; box-shadow: none !important; }
        .win10-input:disabled { background-color: #f3f2f1; color: #71afe5; cursor: not-allowed; border-color: #c8c6c4 !important; }
        .win10-label { font-size: 11px; font-weight: 700; color: #323130; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
        .win10-section-title { font-size: 13px; font-weight: 700; color: #0078d4; border-bottom: 1px solid #eee; padding-bottom: 8px; margin-bottom: 20px; display: flex; align-items: center; gap: 8px; text-transform: uppercase; }
        .win10-btn-save { background: #0078d4; color: #fff; border: none; padding: 10px 25px; border-radius: 0px; font-size: 14px; }
        .win10-btn-save:hover { background: #005a9e; }
        .logo-container { border: 1px solid #e1e1e1; background: #fff; padding: 10px; width: 100%; height: 180px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .key-box { background: #f9f9f9; border: 1px solid #e1e1e1; padding: 15px; }
      `}</style>

      <div className="animate__animated animate__fadeIn">
        <form onSubmit={handleUpdateData}>
          <div className="row g-4">
            <div className="col-lg-12">
              <div className="bg-white border p-4 shadow-sm">
                <div className="win10-section-title">
                  <Building2 size={16} /> {t.sectionId}
                </div>
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                     <div className="bg-white border p-3 shadow-sm mb-3 text-center">
                      <div className="logo-container mb-3">
                        <img
                          src={fotoPreview || (empresa?.logotipo ? `${empresa.logotipo}` : "https://via.placeholder.com/150")}
                          alt="Logo"
                          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        />
                      </div>
                      
                      <label htmlFor="upload-logo" className="btn btn-outline-dark btn-sm rounded-0 w-100 mb-2 d-flex align-items-center justify-content-center gap-2">
                        <Camera size={14} /> {t.btnLogo}
                        <input type="file" id="upload-logo" hidden accept="image/*" onChange={handleFileChange} />
                      </label>

                      {fotoPreview && (
                        <button type="button" onClick={handleUpdatePhoto} disabled={loadingPhoto} className="btn btn-primary btn-sm rounded-0 w-100 mb-2">
                          {loadingPhoto ? t.saving : t.btnConfirmLogo}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4"></div>
                  <div className="col-md-4"></div>
                  <div className="col-md-4">
                    <label className="win10-label">{t.labelLegalName}</label>
                    <input type="text" className="form-control win10-input w-100" name="nome" value={formData.nome} onChange={handleChange} required />
                  </div>
                  <div className="col-md-4">
                    <label className="win10-label">{t.labelNif}</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 rounded-0"><Fingerprint size={14} /></span>
                      <input type="text" className="form-control win10-input border-start-0" name="nif" value={formData.nif} onChange={handleChange} required />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="win10-label">{t.labelEmail}</label>
                    <input type="email" className="form-control win10-input w-100" name="email" value={formData.email} onChange={handleChange} />
                  </div>
                </div>
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <label className="win10-label">{t.labelPhone1}</label>
                    <input type="text" className="form-control win10-input w-100" name="contacto" value={formData.contacto} onChange={handleChange} />
                  </div>
                  <div className="col-md-4">
                    <label className="win10-label">{t.labelPhone2}</label>
                    <input type="text" className="form-control win10-input w-100" name="contacto_alternativo" value={formData.contacto_alternativo} onChange={handleChange} />
                  </div>
                  <div className="col-md-4">
                    <label className="win10-label">{t.labelCountry}</label>
                    <Select options={paises} styles={customSelectStyles} value={paises.find(p => p.value === formData.pais_id)} onChange={(opt) => handleSelectChange("pais_id", opt)} />
                  </div>
                  <div className="col-md-4">
                    <label className="win10-label">{t.labelProvince}</label>
                    <Select options={provincias} styles={customSelectStyles} value={provincias.find(p => p.value === formData.provincia_id)} onChange={(opt) => handleSelectChange("provincia_id", opt)} isDisabled={!formData.pais_id} />
                  </div>
                  <div className="col-md-4">
                    <label className="win10-label">{t.labelIva}</label>
                    <Select options={regimes} styles={customSelectStyles} value={regimes.find(r => r.value === formData.regime_iva_id)} onChange={(opt) => handleSelectChange("regime_iva_id", opt)} />
                  </div>
                  <div className="col-md-4">
                    <label className="win10-label">{t.labelAddress}</label>
                    <input type="text" className="form-control win10-input w-100" name="endereco" value={formData.endereco} onChange={handleChange} />
                  </div>
                </div>

                <div className="text-end pt-3 border-top mt-4">
                  <button type="submit" className="win10-btn-save d-inline-flex align-items-center gap-2 fw-bold" disabled={loading}>
                    {loading ? <span className="spinner-border spinner-border-sm"></span> : <><Save size={18} /> {t.btnUpdate}</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default ProfileEmpresaTab;
